<!--
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import type {LinkModel} from '@/scripts/model/store';
import {computed, ref, unref} from 'vue';
import {Rect} from '@/scripts/geometry';
import {injectSettings} from '@/scripts/settings';
import {injectGameData} from '@/scripts/data';
import {transportSuggestion} from '@/scripts/transport';

const props = defineProps<{
    link?: LinkModel;
}>();

const settings = injectSettings();
const gameData = injectGameData();

//fork addition: in-game transport suggestion for this link's product,
//sized to the link's solved flow (see scripts/transport.ts)
const suggestion = computed(() => {
    const link = props.link;
    const io = link?.output || link?.input;
    if(!link || !io)
        return undefined;
    const productName = io.name;
    const product = productName ? gameData.getGameItem(productName) : undefined;
    const flow = link.flow ?? io.cpsSolvedTotal;
    return transportSuggestion(gameData, product, flow);
});
const mainDivElement = ref<HTMLElement | null>(null);
const computedStyle = computed(() => {
    const link = props.link;
    let middlePoint = link?.buildShape()?.middlePoint;
    const mainDiv = unref(mainDivElement);
    if(!middlePoint || !mainDiv)
        return {};
    let mainDivRect = Rect.assign(mainDiv.getBoundingClientRect());
    if(settings.scale && (settings.scale != 1)) {
        mainDivRect = mainDivRect.scaleSize(1 / settings.scale);
    }
    middlePoint = middlePoint.offsetBy({x: mainDivRect.width / 2, y: mainDivRect.height / 2}, -1);
    return {
        left: middlePoint.x + 'px',
        top: middlePoint.y + 'px',
    };
});
</script>

<template>
    <div
        ref="mainDivElement"
        class="rounded-pill link-connector bg-window-idle hover-border"
        :style="computedStyle"
        @pointerdown.left.stop
        @click.stop
    >
        <icon-component :image="props.link?.input?.image" />
        <link-menu :link="props.link" />
        <div
            v-if="suggestion"
            class="transport-chip"
            :class="{
                'transport-tight': suggestion.warning === 'tight',
                'transport-over': suggestion.warning === 'over',
            }"
            :title="suggestion.title"
        >
            {{ suggestion.label }}
        </div>
    </div>
</template>

<style scoped>
.link-connector {
    position: absolute;
    user-select: none;
    display: flex;
    flex-direction: column;
    align-items: center;
}
/* fork addition: transport suggestion chip under the link's mid icon */
.transport-chip {
    margin-top: 2px;
    padding: 0 6px;
    border-radius: 8px;
    background: rgba(38, 50, 56, 0.85);
    color: #fff;
    font-size: 10px;
    line-height: 16px;
    white-space: nowrap;
    pointer-events: none;
}
/* fork addition: capacity warning states (see transport.ts) — the
   chip is a fixed dark pill in both themes, so the warning colours
   are chosen to read on the board behind it in light and dark mode */
.transport-chip.transport-tight {
    background: rgba(230, 126, 0, 0.95);
    font-weight: 700;
}
.transport-chip.transport-over {
    background: rgba(198, 40, 40, 0.95);
    font-weight: 700;
}
</style>
